import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Archive, Play } from "lucide-react";

// Admin-only control surface for the retention sweep. Flag-only by design:
// nothing here deletes a payroll record.
export const RetentionAdminPanel = () => {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [visible, setVisible] = useState(true);

  const load = useCallback(async () => {
    // Skip the probe entirely for visitors — a public legal page should never
    // fire an authenticated-only request. `user` resolves asynchronously, so
    // recompute visibility on every change rather than latching it off.
    if (user?.role !== "admin") { setVisible(false); return; }
    setVisible(true);
    try { setSummary((await api.get("/legal/retention/summary")).data); }
    catch { setVisible(false); }
  }, [user]);
  useEffect(() => { load(); }, [load]);

  const runScan = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/legal/retention/scan", {});
      toast.success(`Retention sweep complete · ${data.total_flagged} record(s) newly flagged`, {
        description: `Cutoff ${String(data.cutoff).slice(0, 10)} — nothing was deleted.`,
      });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (!visible || !summary) return null;
  return (
    <div className="border-2 border-ink p-5 mt-8 bg-secondary/40" data-testid="retention-admin-panel">
      <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
        <div>
          <div className="overline mb-1">§ ADMIN · DATA PURGE</div>
          <div className="font-heading font-black text-base md:text-lg flex items-center gap-2">
            <Archive className="w-4 h-4" /> {summary.total_purge_eligible} record(s) past the {summary.retention_years}-year window
          </div>
          <div className="font-mono text-[10px] text-muted-foreground mt-1">
            Cutoff {String(summary.cutoff).slice(0, 10)}
            {summary.last_scan_at ? ` · last swept ${new Date(summary.last_scan_at).toLocaleString()}` : " · never swept"}
          </div>
        </div>
        <button
          onClick={runScan}
          disabled={busy}
          data-testid="retention-scan-btn"
          className="hard-btn px-4 py-2 font-mono uppercase text-[10px] tracking-widest inline-flex items-center gap-2 disabled:opacity-40"
        >
          <Play className="w-3 h-3" /> {busy ? "Sweeping…" : "Run Retention Sweep"}
        </button>
      </div>
      <div className="border-2 border-ink overflow-x-auto bg-white">
        <table className="w-full min-w-[520px] font-mono text-[11px]" data-testid="retention-admin-table">
          <thead className="bg-ink text-white">
            <tr>
              <th className="text-left px-3 py-2 uppercase tracking-widest">Record set</th>
              <th className="text-right px-3 py-2 uppercase tracking-widest">Total</th>
              <th className="text-right px-3 py-2 uppercase tracking-widest">Purge-eligible</th>
              <th className="text-right px-3 py-2 uppercase tracking-widest">Expiring &lt; 6mo</th>
            </tr>
          </thead>
          <tbody>
            {summary.collections.map((c, i) => (
              <tr key={c.collection} className={i % 2 ? "bg-[#F9F9F9]" : "bg-white"} data-testid={`retention-row-${c.collection}`}>
                <td className="px-3 py-2 border-t border-ink/20 font-semibold">{c.collection}</td>
                <td className="px-3 py-2 border-t border-ink/20 text-right">{c.total}</td>
                <td className={`px-3 py-2 border-t border-ink/20 text-right font-black ${c.purge_eligible ? "text-bad" : ""}`}>{c.purge_eligible}</td>
                <td className="px-3 py-2 border-t border-ink/20 text-right">{c.approaching_6mo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="font-mono text-[10px] text-muted-foreground mt-2 leading-relaxed">{summary.policy}</div>
    </div>
  );
};
